// A discreet banner under the top bar while the live connection (SSE, /api/events) has been lost for
// more than a few seconds: "Connexion perdue — reconnexion…". It goes away as soon as the stream
// is open again (the shell then resyncs and says "Connexion rétablie.").
import { h } from '../dom.js';
import { t } from '../i18n/fr.js';

/** How long the link may be down before the banner shows (short blips stay silent). */
export const LOST_AFTER_MS = 5000;

/** Connection states (web/api.js connectEvents) during which live updates are not arriving. */
const DOWN = new Set(['connecting', 'reconnecting', 'offline']);

/**
 * True when an SSE status means live updates are not arriving (pure). `idle` (not started),
 * `closed` (the app is leaving) and `unsupported` (no EventSource: nothing to reconnect) are not
 * a lost connection.
 * @param {string} status
 */
export function isConnectionDown(status) {
  return DOWN.has(status);
}

/**
 * Creates the banner and follows `store.sse`.
 * @param {{store: {get(): object, on(key: string, fn: Function): () => void}}} ctx
 * @param {{host: HTMLElement, delayMs?: number}} opts
 * @returns {{el: HTMLElement, isShown(): boolean, destroy(): void}}
 */
export function createConnectionBanner(ctx, { host, delayMs = LOST_AFTER_MS }) {
  const { store } = ctx;
  const el = h('div', { class: 'conn-banner', role: 'status', 'aria-live': 'polite', hidden: true },
    h('span', { class: 'conn-banner-dot', 'aria-hidden': 'true' }), h('span', {}, t('app.connectionLost')));
  host.appendChild(el);
  let timer = null;

  function apply(status) {
    if (!isConnectionDown(status)) {
      clearTimeout(timer);
      timer = null;
      el.hidden = true;
      return;
    }
    if (!el.hidden || timer) return;
    timer = setTimeout(() => {
      timer = null;
      if (isConnectionDown(store.get().sse)) el.hidden = false;
    }, delayMs);
  }

  const off = store.on('sse', (status) => apply(status));
  apply(store.get().sse);
  return {
    el,
    isShown: () => !el.hidden,
    destroy() {
      off();
      clearTimeout(timer);
      el.remove();
    },
  };
}
